import styled, { css } from 'styled-components';
import { Spacing8 } from 'autogen/design-tokens/tokens';
import type { FooterMenuItem, IconType, WebsiteIcon as WebsiteIconEnum } from 'autogen/swagger/all';
import IconCard from 'components/generic/iconCard';
import Icon from 'components/Icon';
import WebsiteIcon from 'components/Icon/WebsiteIcon';
import PageAction from 'components/shared/Routing/PageAction';
import { stringToKey } from 'modules/helpers/strings';
import { breakpoint, colors, sizes } from 'variables';

const Container = styled.div`
    display: flex;
    flex-wrap: wrap;
`;

const ItemStyle = css<{ $isIcon: boolean }>`
    color: ${colors.negative};

    ${({ $isIcon }) =>
        $isIcon
            ? css`
                  margin: ${Spacing8} ${Spacing8} 0 0;
              `
            : css`
                  width: 100%;
                  padding: ${Spacing8} 0;

                  &:first-child {
                      margin-top: ${Spacing8};
                  }

                  ${breakpoint.up(sizes.lg)} {
                      padding: 0;

                      &:first-child {
                          margin-top: 0;
                      }
                  }
              `}
`;

const Wrapper = styled.div<{ $isIcon: boolean }>`
    ${ItemStyle}
`;

const StyledPageAction = styled(PageAction)<{ $isIcon: boolean }>`
    ${ItemStyle}

    &:not([href]):not([tabindex]),
    &:not([href]):not([tabindex]):hover,
    &:hover,
    &:focus {
        color: ${colors.negative};
    }
`;

const AppStoreIcon = styled(WebsiteIcon)`
    width: 118px;
`;

const PlayStoreIcon = styled(WebsiteIcon)`
    width: 135px;
`;

interface IProps {
    items: FooterMenuItem[];
}

function getItem(
    text: string,
    icon: WebsiteIconEnum | undefined,
    iconType: IconType | undefined
): JSX.Element | string | null {
    if (icon === 'AppStore_Apple') return <AppStoreIcon icon={icon} aria-label={text} />;
    if (icon === 'AppStore_GooglePlay') return <PlayStoreIcon icon={icon} aria-label={text} />;

    return icon || iconType ? (
        <IconCard size="lg">
            {iconType ? (
                <Icon type={iconType} size={24} aria-label={text} />
            ) : (
                <WebsiteIcon icon={icon} aria-label={text} />
            )}
        </IconCard>
    ) : (
        text
    );
}

export default function NavigationFooterItems(props: IProps): JSX.Element {
    const { items } = props;

    return (
        <Container>
            {items.map((item) => {
                const { text, link, icon, iconType } = item;

                return link ? (
                    <StyledPageAction action={link} key={stringToKey(text)} $isIcon={!!icon || !!iconType}>
                        {getItem(text, icon, iconType)}
                    </StyledPageAction>
                ) : (
                    <Wrapper key={stringToKey(text)} $isIcon={!!icon || !!iconType}>
                        {getItem(text, icon, iconType)}
                    </Wrapper>
                );
            })}
        </Container>
    );
}
